// src/features/staff/hooks/useStaffFolder.js
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFolders } from '../../files/hooks/useFolders';

const STAFF_FOLDER_NAME = 'i18n-files:i18n_folder_names.staff';

/**
 * Find the Staff meta folder at root, then the subfolder named after the staff
 * member. Returns { folderId, breadcrumbs } for use as an upload target or
 * SelectFileDialog starting point, or null if anything fails (the dialog then
 * just opens at root).
 *
 * With `{ create: true }` (uploading) missing folders are created. Without it
 * (just browsing) nothing is created: if the staff member has no folder yet the
 * Staff folder is returned instead, so opening a dialog doesn't leave empty
 * folders behind.
 */
export function useStaffFolder() {
  const { t } = useTranslation(['files']);
  const { getRootContents, createFolder, getFolderContents } = useFolders();

  return useCallback(async (staffName, { create = false } = {}) => {
    try {
      const rootResult = await getRootContents();
      if (!rootResult?.success) throw new Error('Failed to fetch root contents');

      let staffMetaFolderId;
      const existingMeta = rootResult.data.data.folders?.find(f => f.folder_name === STAFF_FOLDER_NAME);
      if (existingMeta) {
        staffMetaFolderId = existingMeta.folder_id;
      } else if (create) {
        const createResult = await createFolder({
          folder_name: STAFF_FOLDER_NAME,
          description: 'System folder for staff files organized by staff member name',
          parent_folder_id: null,
          is_system_folder: false,
          is_protected: false,
        });
        if (!createResult?.success) throw new Error('Failed to create Staff folder');
        staffMetaFolderId = createResult.data.data.folder_id;
      } else {
        return null;
      }

      const metaCrumbs = [
        { id: null, name: t('files:root') },
        { id: staffMetaFolderId, name: t('files:i18n_folder_names.staff') },
      ];

      const contentsResult = await getFolderContents(staffMetaFolderId);
      if (!contentsResult?.success) throw new Error('Failed to fetch Staff folder contents');

      let staffFolderId;
      const existing = (contentsResult.data.data?.subfolders || []).find(f => f.folder_name === staffName);
      if (existing) {
        staffFolderId = existing.folder_id;
      } else if (create) {
        const createResult = await createFolder({
          folder_name: staffName,
          description: `Files for staff member: ${staffName}`,
          parent_folder_id: staffMetaFolderId,
          is_system_folder: false,
          is_protected: false,
        });
        if (!createResult?.success) throw new Error('Failed to create staff member folder');
        staffFolderId = createResult.data.data.folder_id;
      } else {
        return { folderId: staffMetaFolderId, breadcrumbs: metaCrumbs };
      }

      return {
        folderId: staffFolderId,
        breadcrumbs: [...metaCrumbs, { id: staffFolderId, name: staffName }],
      };
    } catch (err) {
      console.error('Error ensuring staff folder:', err);
      return null;
    }
  }, [getRootContents, createFolder, getFolderContents, t]);
}
